import {
  MultiFactorAuthenticationChallengeError,
  MultiFactorAuthenticationCodeError,
  type MultiFactorAuthenticationError,
  MultiFactorAuthenticationPasskeysError,
} from '@pba/client-auth';
import { startAuthentication } from '@simplewebauthn/browser';
import { useRelyingPartyId } from 'lib/webauthn';
import { useCallback, useMemo } from 'react';
import { handleAxiosError, message } from 'ui';
import { useSetIsAuthenticated } from 'features/auth';
import { useLoginToNodeMutation } from 'features/users';
import { useEmailCodeLoginModal } from './useEmailCodeLoginModal';
import { usePasskeyLoginModal } from './usePasskeyLoginModal';

type P = {
  onNavigateToCheckCode: ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => void;
  onSuccessLogin: () => Promise<void>;
};

export const useHandleMfaError = ({ onNavigateToCheckCode, onSuccessLogin }: P) => {
  const { contextHolder: emailModalContext, showEmailCodeLoginModal } = useEmailCodeLoginModal();
  const { contextHolder: passkeyModalContext, showPasskeyLoginModal } = usePasskeyLoginModal();

  const getRelyingPartyId = useRelyingPartyId();
  const setIsAuthenticated = useSetIsAuthenticated();
  const { isPending, mutate: nodeLogin } = useLoginToNodeMutation();

  const contextHolder = useMemo(
    () => (
      <>
        {emailModalContext}
        {passkeyModalContext}
      </>
    ),
    [emailModalContext, passkeyModalContext],
  );

  const completeWithEmailCode = useCallback(
    ({ error }: { error: MultiFactorAuthenticationChallengeError }) => {
      nodeLogin(
        { strategy: 'code', token: error.axiosError.response.data.data.token },
        {
          onError(getChallengeError) {
            if (getChallengeError instanceof MultiFactorAuthenticationCodeError) {
              const token = getChallengeError.axiosError.response.data.data.token;
              onNavigateToCheckCode({ tokenToGetCode: error.axiosError.response.data.data.token, tokenToLogin: token });
            } else {
              handleAxiosError(getChallengeError);
            }
          },
        },
      );
    },
    [nodeLogin, onNavigateToCheckCode],
  );

  const completeWithPasskey = useCallback(
    ({ error }: { error: MultiFactorAuthenticationChallengeError }) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- External API
      const relyingPartyID = getRelyingPartyId();

      const tokenFromChallenge = error.axiosError.response.data.data.token;

      nodeLogin(
        {
          relyingPartyID,
          strategy: 'passkey',
          token: tokenFromChallenge,
        },
        {
          onError(challengeError) {
            if (challengeError instanceof MultiFactorAuthenticationPasskeysError) {
              void (async () => {
                const { options, token } = challengeError.axiosError.response.data.data;
                const authenticationResponse = await startAuthentication({
                  optionsJSON: options,
                });

                nodeLogin(
                  {
                    response: authenticationResponse,
                    strategy: 'passkey',
                    token,
                  },
                  {
                    onError() {
                      message.error('Authentication with passkey failed. Try again later.');
                    },
                    async onSuccess() {
                      setIsAuthenticated(true);
                      await onSuccessLogin();
                    },
                  },
                );
              })();
            } else {
              handleAxiosError(challengeError);
            }
          },
        },
      );
    },
    [getRelyingPartyId, nodeLogin, onSuccessLogin, setIsAuthenticated],
  );

  const handleError = useCallback(
    ({ error }: { error: MultiFactorAuthenticationError }) => {
      if (error instanceof MultiFactorAuthenticationChallengeError) {
        const strategySet = new Set(error.axiosError.response.data.data.strategies);
        const hasCode = strategySet.has('code');
        const hasPasskey = strategySet.has('passkey');
        const hasPasskeyAndCode = hasPasskey && hasCode;

        if (hasPasskeyAndCode) {
          showPasskeyLoginModal({
            onLoginWithPasskey() {
              completeWithPasskey({ error });
            },
            onSendPassword() {
              completeWithEmailCode({ error });
            },
          });
        } else if (hasCode) {
          showEmailCodeLoginModal({
            onSendPassword() {
              completeWithEmailCode({ error });
            },
          });
        } else {
          handleAxiosError(error);
        }
      } else {
        handleAxiosError(error);
      }
    },
    [completeWithEmailCode, completeWithPasskey, showEmailCodeLoginModal, showPasskeyLoginModal],
  );

  return {
    contextHolder,
    handleError,
    isMfaLoginLoading: isPending,
    showEmailCodeLoginModal,
    showPasskeyLoginModal,
  };
};
